import React, { useMemo, useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Field, Formik, FormikProps } from 'formik';
import _ from 'lodash';
import { CountryType, StateType, SupportGuarantor, CreateClaimGuarantor } from '../../../../api';
import { getOptions } from '../../../base/select';
import * as S from '../form.styles';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import { InputField, SelectField } from '../../../base/form';
import { AsyncSelectField } from '../../../base/form/async-select/async-select';
import { DatePickerField } from '../../../base/form/datepicker/date-picker';
import * as Yup from 'yup';
import { FormErrorBanner } from '../../../base/form/form-error-banner';


interface GuarantorFormValues {
    guarantorLookupLastName?: string;
    guarantorId?: string;
    first: string;
    middle?: string;
    last: string;
    dob?: string;
    phone?: string;
    email?: string;
    organization?: string;
    externalOrganizationId?: string;
    address1: string;
    address2?: string;
    city: string;
    state?: StateType | '';
    country?: CountryType | '';
    postal: string;
}

const emptyValues: GuarantorFormValues = {
    guarantorLookupLastName: '',
    guarantorId: '',
    first: '',
    middle: '',
    last: '',
    dob: '',
    phone: '',
    email: '',
    organization: '',
    externalOrganizationId: '',
    address1: '',
    address2: '',
    city: '',
    state: '' as any,
    country: CountryType.US as any,
    postal: '',
};

interface IGuarantorFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const GuarantorForm: React.FunctionComponent<IGuarantorFormProps> = observer(({ claimEditorStore }) => {
    const stateOptions = getOptions(StateType);
    const countryOptions = getOptions(CountryType);
    const formikRef = useRef<FormikProps<GuarantorFormValues> | null>(null);
    const lookupDebounceRef = useRef<NodeJS.Timeout | null>(null);

    const GuarantorSchema = Yup.object().shape({
        first: Yup.string().required('First name is required'),
        middle: Yup.string(),
        last: Yup.string().required('Last name is required'),
        dob: Yup.string(),
        phone: Yup.string(),
        email: Yup.string().email('Invalid email'),
        organization: Yup.string(),
        externalOrganizationId: Yup.string(),
        address1: Yup.string().required('Address Line 1 is required'),
        address2: Yup.string(),
        city: Yup.string().required('City is required'),
        state: Yup.mixed<StateType | ''>().required('State is required'),
        country: Yup.mixed<CountryType | ''>().required('Country is required'),
        postal: Yup.string().required('Postal Code is required'),
    });

    const initialValues = useMemo<GuarantorFormValues>(() => {
        const payload = (claimEditorStore?.activePayload as any) || {};
        const g: CreateClaimGuarantor | undefined = (claimEditorStore as any)?.guarantor || payload.guarantor;
        if (!g) return emptyValues;
        const addr = g.addresses?.[0];
        return {
            guarantorLookupLastName: g.last || '',
            guarantorId: (g as any).guarantorId || (g as any).id || '',
            first: g.first || '',
            middle: g.middle || '',
            last: g.last || '',
            dob: (g.dob as any) || '',
            phone: g.phone || '',
            email: g.email || '',
            organization: g.organization || '',
            externalOrganizationId: (g as any).externalOrganizationId || '',
            address1: addr?.lines?.[0] || '',
            address2: addr?.lines?.[1] || '',
            city: addr?.city || '',
            state: (addr?.state as any) || '' as any,
            country: (addr?.country as any) || CountryType.US as any,
            postal: addr?.postal || '',
        };
    }, [claimEditorStore?.activePayload, (claimEditorStore as any)?.guarantor]);

    const convertToGuarantor = (v: GuarantorFormValues): CreateClaimGuarantor => {
        const lines = [v.address1].filter(Boolean);
        if (v.address2) lines.push(v.address2);
        return {
            guarantorId: v.guarantorId || undefined,
            first: v.first || '',
            middle: v.middle || undefined as any,
            last: v.last || '',
            dob: (v.dob as any) || undefined,
            phone: v.phone || undefined,
            email: v.email || undefined,
            organization: v.organization || undefined,
            externalOrganizationId: v.externalOrganizationId || undefined,
            addresses: [
                {
                    name: v.organization || `${(v.first || '').trim()} ${(v.last || '').trim()}`.trim(),
                    lines,
                    city: v.city || '',
                    state: (v.state as any) || undefined,
                    postal: v.postal || '',
                    country: (v.country as any) || undefined,
                } as any,
            ],
        } as CreateClaimGuarantor;
    };

    const persistGuarantor = (values: GuarantorFormValues): void => {
        claimEditorStore.updateGuarantor(convertToGuarantor(values));
    };

    const debouncedSave = useMemo(
        () =>
            _.debounce(() => {
                const latestValues = formikRef.current?.values;
                if (!latestValues) return;
                persistGuarantor(latestValues);
            }, 500),
        [claimEditorStore],
    );

    useEffect(() => {
        return () => {
            debouncedSave.cancel();
            if (lookupDebounceRef.current) clearTimeout(lookupDebounceRef.current);
        };
    }, [debouncedSave]);

    return (
        <Formik<GuarantorFormValues>
            innerRef={formikRef as any}
            initialValues={initialValues}
            validationSchema={GuarantorSchema}
            onSubmit={() => {}}
            enableReinitialize
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={false}
        >
            {formik => {
                // Save initial values to store on mount if store is null/empty
                const hasInitializedRef = useRef(false);
                useEffect(() => {
                    if (!hasInitializedRef.current) {
                        const storeGuarantor = claimEditorStore?.guarantor;
                        // Only save if store is null/empty and we have initial values from payload
                        if (!storeGuarantor && initialValues && (initialValues.first || initialValues.last || initialValues.organization)) {
                            persistGuarantor(initialValues);
                        }
                        hasInitializedRef.current = true;
                    }
                }, []); // Only run once on mount

                useEffect(() => {
                    if (formik.dirty) {
                        debouncedSave();
                    }
                }, [formik.values, formik.dirty, debouncedSave]);

                useEffect(() => {
                    if (claimEditorStore.validationSignal > 0) {
                        // Flush pending guarantor updates so submit validation reads latest values.
                        debouncedSave.cancel();
                        persistGuarantor(formik.values);
                        formik.submitForm();
                    }
                }, [claimEditorStore.validationSignal, debouncedSave]);

                return (
                <S.FormContainer>
                    <FormErrorBanner submitCount={formik.submitCount} isValid={formik.isValid} errors={formik.errors} />
                    <S.FormGrid>
                        <S.FormField span={2}>
                            <S.Label>Guarantor Lookup</S.Label>
                            <Field
                                name="guarantorLookup"
                                component={AsyncSelectField}
                                placeholder="Type last name to search"
                                loadOptions={async (q: string) => {
                                    const results: SupportGuarantor[] = await claimEditorStore.searchGuarantorsByLastName(q);
                                    return results.map(g => ({
                                        label: [g.first, g.middle, g.last].filter(Boolean).join(' ') || (g.organization || ''),
                                        value: g.id as any,
                                        guarantor: g,
                                        info: ' | ' + (g?.phone || '') + ' | ' + (g?.email ? _.startCase(_.lowerCase(g.email)) : ''),
                                    } as any));
                                }}
                                onChange={(opt: any, form: any) => {
                                    const g: SupportGuarantor = opt?.guarantor;
                                    if (!g) {
                                        form.setFieldValue('guarantorId', undefined);
                                        return;
                                    }
                                    const addr = g.addresses?.[0];
                                    const newValues = {
                                        guarantorLookupLastName: g.last || '',
                                        guarantorId: g.id || '',
                                        first: g.first || '',
                                        middle: g.middle || '',
                                        last: g.last || '',
                                        dob: (g.dob as any) || '',
                                        phone: g.phone || '',
                                        email: g.email || '',
                                        organization: g.organization || '',
                                        externalOrganizationId: (g as any).externalOrganizationId || '',
                                        address1: addr?.lines?.[0] || '',
                                        address2: addr?.lines?.[1] || '',
                                        city: addr?.city || '',
                                        state: (addr?.state as any) || '' as any,
                                        country: (addr?.country as any) || '' as any,
                                        postal: addr?.postal || '',
                                    };
                                    form.setValues(newValues);
                                    persistGuarantor(newValues);
                                }}
                            />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>First Name</S.Label>
                            <Field name="first" placeholder="First Name" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Middle Name</S.Label>
                            <Field name="middle" placeholder="Middle Name" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Last Name</S.Label>
                            <Field name="last" placeholder="Last Name" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Date of Birth</S.Label>
                            <Field name="dob" placeholder="YYYY-MM-DD" component={DatePickerField} />
                        </S.FormField>

                        <S.FormField>
                            <S.Label>Phone Number</S.Label>
                            <Field
                                name="phone"
                                placeholder="Phone Number"
                                component={InputField}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label>Email</S.Label>
                            <Field name="email" placeholder="Email" component={InputField} type="email" />
                        </S.FormField>

                        <S.FormField span={2}>
                            <S.Label>Organization Name</S.Label>
                            <Field name="organization" placeholder="Organization Name" component={InputField} />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>External Organization System ID</S.Label>
                            <Field name="externalOrganizationId" placeholder="External System ID" component={InputField} />
                        </S.FormField>

                        <S.FormField span={2}>
                            <S.Label required>Address Line 1</S.Label>
                            <Field name="address1" placeholder="Address Line 1" component={InputField} />
                        </S.FormField>
                        <S.FormField span={2}>
                            <S.Label>Address Line 2</S.Label>
                            <Field name="address2" placeholder="Address Line 2" component={InputField} />
                        </S.FormField>

                        <S.FormField>
                            <S.Label required>City</S.Label>
                            <Field name="city" placeholder="City" component={InputField} />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>State</S.Label>
                            <Field
                                name="state"
                                component={SelectField}
                                options={stateOptions as any}
                                placeholder="Select State"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Country</S.Label>
                            <Field
                                name="country"
                                component={SelectField}
                                options={countryOptions as any}
                                placeholder="Select Country"
                                isClearable={false}
                            />
                        </S.FormField>
                        <S.FormField>
                            <S.Label required>Postal Code</S.Label>
                            <Field name="postal" placeholder="Postal Code" component={InputField} />
                        </S.FormField>
                    </S.FormGrid>
                </S.FormContainer>
                );
            }}
        </Formik>
    );
});

export default GuarantorForm;
